import { Link } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuGroup,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@lawline/ui/components/dropdown-menu";

import { authClient } from "@platform/auth/client";
import {
	sessionMutationOptions,
	useSessionTransition,
} from "@platform/auth/session-transition.context";

// Active-tenant switcher in the sidebar header. Clipped with the sidebar
// when collapsed (the label fades like the nav labels), shown on hover.
export function TenantSwitcher() {
	const t = useTranslations("common.tenantSwitcher");
	const changeSession = useSessionTransition();
	const { data: tenants } = authClient.useListOrganizations();
	const { data: active } = authClient.useActiveOrganization();

	if (!tenants || tenants.length === 0) return null;

	async function select(id: string) {
		if (id === active?.id) return;
		await changeSession(async () => {
			const { data, error } = await authClient.organization.setActive({
				organizationId: id,
				fetchOptions: sessionMutationOptions,
			});
			if (error || data?.id !== id) throw new Error("Organization switch is unconfirmed");
			return "reload";
		});
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="h-8 min-w-0 flex-1 justify-between gap-2 px-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-has-[[data-state=open]]:opacity-100"
				>
					<span className="truncate text-xs">{active?.name ?? t("placeholder")}</span>
					<ChevronsUpDown className="text-muted-foreground size-3.5 shrink-0" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-48">
				<DropdownMenuGroup>
					{tenants.map((tenant) => (
						<DropdownMenuItem key={tenant.id} onClick={() => select(tenant.id)}>
							<span className="truncate">{tenant.name}</span>
							{tenant.id === active?.id && <Check className="ml-auto size-4" />}
						</DropdownMenuItem>
					))}
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem asChild>
						<Link to="/tenant" search={{ create: true }}>
							<Plus className="size-4" />
							{t("newTenant")}
						</Link>
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
